import seo from "config/seo.json";

type SeoPage = {
    title: string;
    description: string;
    keywords?: string;
    ogImage?: string;
    type?: string;
    noindex?: boolean;
};

type SeoRoute = {
    type: "exact" | "prefix";
    path: string;
    key: string;
};

const routes = seo.routes as SeoRoute[];
const pages = seo.pages as Record<string, SeoPage>;

export const getSeoKey = (pathname: string): string => {
    const match = routes.find((route) =>
        route.type === "exact"
            ? pathname === route.path
            : pathname === route.path || pathname.startsWith(`${route.path}/`)
    );

    return match ? match.key : "notFound";
};

export const getSeo = (pathname: string) => {
    const page = pages[getSeoKey(pathname)];
    const canonical = `${seo.siteUrl}${pathname === "/" ? "" : pathname}`;

    return {
        ...page,
        canonical,
        image: `${seo.siteUrl}${page.ogImage ?? seo.ogImage}`,
        siteName: "Henry Adedugba",
    };
};

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
    let tag = document.head.querySelector<HTMLMetaElement>(
        `meta[${attr}="${key}"]`
    );

    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
    }

    tag.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
    let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
    }

    tag.setAttribute("href", href);
};

/**
 * Keeps title/description/OG/Twitter tags in sync with the current route.
 *
 * Crawlers that execute JavaScript will pick this up, but most social scrapers
 * (Slack, LinkedIn, iMessage, X) do not. `scripts/generate-route-meta.js` bakes
 * static per-route HTML at build time for those.
 */
const useDocumentMeta = (pathname: string) => {
    const meta = getSeo(pathname);

    document.title = meta.title;
    upsertMeta("name", "description", meta.description);

    if (meta.keywords) {
        upsertMeta("name", "keywords", meta.keywords);
    } else {
        document.head.querySelector('meta[name="keywords"]')?.remove();
    }

    upsertMeta("name", "robots", meta.noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", meta.canonical);

    upsertMeta("property", "og:site_name", meta.siteName);
    upsertMeta("property", "og:type", meta.type ?? "website");
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:url", meta.canonical);
    upsertMeta("property", "og:image", meta.image);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", meta.title);
    upsertMeta("name", "twitter:description", meta.description);
    upsertMeta("name", "twitter:image", meta.image);

    if (seo.twitterHandle) {
        upsertMeta("name", "twitter:site", seo.twitterHandle);
    }
};

export default useDocumentMeta;
